import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, Heart, Leaf } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { formatEUR } from '../../utils/format';

/**
 * HaargummiShowcase — eigenständige Sektion auf der Homepage.
 * Hebt die Vielfalt an Haargummi-Varianten hervor. Bewusst KEINE
 * "handmade"-Betonung. Professioneller, ruhiger Look mit Brand-Brauntönen.
 *
 * Layout (überarbeitet):
 *  - Horizontaler Scroll-Slider wie die Kategorien-Sektion
 *  - Storytelling-Sektion: 3 Mini-Tiles erklären warum gute Haargummis wichtig sind
 *  - Chevron-Buttons zum Steppen + Snap-Scroll für saubere mobile UX
 */
const HaargummiShowcase = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/products', { params: { limit: 200 } });
        const all = Array.isArray(res.data) ? res.data : (res.data.products || []);
        const filtered = all.filter((p) => {
          const sub = (p.subcategory || '').trim().toLowerCase();
          const name = (p.name || '').toLowerCase();
          return sub === 'haargummi' || name.startsWith('haargummi');
        });
        setItems(filtered.slice(0, 10));
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!loading && items.length === 0) return null;

  const scrollBy = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Scroll one tile-width (~220px) at a time
    const amount = Math.max(220, el.clientWidth * 0.6);
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <section
      className="py-8 sm:py-10 px-4"
      style={{ background: '#FAF5EE' }}
      data-testid="haargummi-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header — kompakt, links ausgerichtet */}
        <div className="mb-5 sm:mb-6 max-w-2xl">
          <div
            className="text-[10px] sm:text-xs tracking-[4px] mb-2"
            style={{ color: '#C4A084' }}
          >
            KOLLEKTION
          </div>
          <h2
            className="text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight mb-2"
            style={{ color: '#5D4E3C', fontFamily: 'Georgia, serif' }}
          >
            Haargummis, die dein Haar lieben werden
          </h2>
          <div className="w-12 h-px mb-3" style={{ background: '#C4A084' }} />
          <p
            className="text-xs sm:text-sm leading-relaxed"
            style={{ color: '#6D5A47' }}
          >
            Samtig, gestrickt oder pastellfarben — sorgfältig ausgewählte
            Haargummis für jeden Anlass.
          </p>
        </div>

        {/* Storytelling — kompakt, 3 inline-Punkte mit Icons */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-6 sm:mb-7">
          <div className="flex items-center gap-2 min-w-0" data-testid="haargummi-story-1">
            <Heart className="h-4 w-4 flex-shrink-0" style={{ color: '#C4A084' }} />
            <span className="text-xs sm:text-sm" style={{ color: '#5D4E3C' }}>
              Sanft zum Haar
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0" data-testid="haargummi-story-2">
            <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: '#C4A084' }} />
            <span className="text-xs sm:text-sm" style={{ color: '#5D4E3C' }}>
              Mit Style
            </span>
          </div>
          <div className="flex items-center gap-2 min-w-0" data-testid="haargummi-story-3">
            <Leaf className="h-4 w-4 flex-shrink-0" style={{ color: '#C4A084' }} />
            <span className="text-xs sm:text-sm" style={{ color: '#5D4E3C' }}>
              Liebevoll ausgewählt
            </span>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="flex gap-3 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex-shrink-0"
                style={{ width: '130px', height: '160px', background: '#F0E6D6' }}
              />
            ))}
          </div>
        )}

        {/* Horizontal Scroller */}
        {!loading && items.length > 0 && (
          <>
            <div className="relative">
              {/* Chevron buttons (desktop only) */}
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label="Vorherige"
                className="hidden md:flex absolute -left-3 top-[30%] z-10 w-8 h-8 items-center justify-center bg-white shadow-md hover:bg-[#FAF5EE] transition-colors"
                style={{ border: '1px solid #E8DCC8' }}
                data-testid="haargummi-scroll-prev"
              >
                <ChevronLeft className="h-4 w-4" style={{ color: '#5D4E3C' }} />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label="Nächste"
                className="hidden md:flex absolute -right-3 top-[30%] z-10 w-8 h-8 items-center justify-center bg-white shadow-md hover:bg-[#FAF5EE] transition-colors"
                style={{ border: '1px solid #E8DCC8' }}
                data-testid="haargummi-scroll-next"
              >
                <ChevronRight className="h-4 w-4" style={{ color: '#5D4E3C' }} />
              </button>

              <div
                ref={scrollerRef}
                className="overflow-x-auto scrollbar-hide pb-1"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  marginLeft: '-16px',
                  marginRight: '-16px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                }}
                data-testid="haargummi-scroller"
              >
                <div style={{ display: 'flex', gap: '12px', width: 'max-content' }}>
                  {items.map((p) => (
                    <Link
                      key={p.id}
                      to={`/product/${p.id}`}
                      className="group block"
                      style={{
                        flexShrink: 0,
                        width: '130px',
                        scrollSnapAlign: 'start',
                      }}
                      data-testid={`haargummi-card-${p.id}`}
                    >
                      <div
                        className="relative overflow-hidden mb-2"
                        style={{
                          width: '130px',
                          height: '130px',
                          background: '#fff',
                          border: '1px solid #E8DCC8',
                        }}
                      >
                        {p.images && p.images[0] ? (
                          <img
                            src={p.images[0]}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center text-[10px]"
                            style={{ color: '#A89A85' }}
                          >
                            Kein Bild
                          </div>
                        )}
                      </div>

                      <h3
                        className="text-xs font-medium leading-tight mb-0.5 group-hover:underline truncate"
                        style={{ color: '#5D4E3C' }}
                      >
                        {p.name?.replace(/^Haargummi\s*/i, '').trim() || p.name}
                      </h3>
                      <p className="text-xs" style={{ color: '#C4A084' }}>
                        {typeof p.price === 'number'
                          ? `${formatEUR(p.price)}`
                          : `${formatEUR(p.price)}`}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Subtle hint that more posts exist */}
            <p
              className="md:hidden text-center text-[10px] tracking-widest mt-3"
              style={{ color: '#A89A85' }}
            >
              ← SEITLICH WISCHEN →
            </p>

            {/* CTA */}
            <div className="mt-6 sm:mt-8 text-center">
              <Link
                to="/shop?category=Haar%20Accessoires&subcategory=Haargummi"
                className="inline-block px-6 py-2 text-[11px] tracking-[3px] transition-colors duration-200"
                style={{
                  color: '#5D4E3C',
                  border: '1px solid #C4A084',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#5D4E3C';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#5D4E3C';
                }}
                data-testid="haargummi-shop-all"
              >
                ALLE HAARGUMMIS ANSEHEN
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default HaargummiShowcase;
